import React, { useEffect, useState } from "react";
import "@/styles/Layout.scss";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import Spinner from "@/components/Spinner";
import { getUser } from "@/hooks/useLocalStorage";
import apiClient from "@/services";
import { useNavigate } from "react-router-dom";
import ImageUploadForm from "@/components/ImageUploadForm";
import { handleImageUpload } from "@/scripts/uploadImages";
import getConstants from "@/scripts/getConstants";

const formSchema = z.object({
  grade: z.string().min(2, { message: "Grade of material is required" }),
  form: z.string().min(2, { message: "Form of material is required" }),
  price: z.string().min(1, { message: "Price is required" }),
  priceUnit: z.string().min(2, { message: "Unit is required" }),
  quantity: z.string().min(1, { message: "Quantity is required" }),
  quantityUnit: z.string().min(2, { message: "Unit is required" }),
  location: z.string().optional(),
  source: z.string().optional(),
  postalCode: z.string().optional(),
  description: z.string().optional(),
  terms: z.boolean().refine((value) => value === true, {
    message: "Please accept the terms and conditions to proceed.",
  }),
});

function SellerProductForm() {
  const user = getUser();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([{ file: null, description: "" }]);
  // Constants
  const [forms, setForms] = useState([]);
  const [grades, setGrades] = useState([]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      grade: "",
      form: "",
      price: "",
      priceUnit: "USD/kg",
      quantity: "",
      quantityUnit: "kg",
      location: "",
      source: "",
      postalCode: "",
      description: "",
      terms: false,
    },
  });

  const onSubmit = async (values) => {
    // Clients email check - for Seller Agents
    if ((user.role === "seller_agent" || user.role === "admin") && !email) {
      alert("Client's email is required!");
      return;
    }

    // Filter out empty images
    const validImages = images.filter(
      (image) => image.file && image.description.trim() !== ""
    );

    // Check number of images
    if (validImages.length < 1) {
      alert("Product image is required!");
      return;
    }

    // Check number of images
    if (validImages.length > 5) {
      alert("You can only upload up to 5 images!");
      return;
    }

    try {
      setLoading(true);

      let imageData;
      // Upload images and handle errors explicitly
      try {
        imageData = await handleImageUpload(
          validImages,
          "seller-products",
          user.uid
        );
      } catch (error) {
        alert("Error uploading images: " + (error.message || error));
        throw error; // Stop further execution if image upload fails
      }

      // Validate imageData
      if (!imageData || !Array.isArray(imageData) || imageData.length === 0) {
        throw new Error("Failed to upload images");
      }

      // Send form data along with the uploaded image URLs
      const { status } = await apiClient.post("/createSellerRequest", {
        product: { ...values, images: imageData },
        email: email || "", // for agents to set user_id
        userRole: user.role,
      });

      if (status === 201) {
        alert("Product form submitted successfully!");
        console.log("Seller request created successfully");
        navigate("/home");
      } else {
        throw new Error(`Unexpected response status: ${status}`);
      }
    } catch (error) {
      if (error.status === 400) {
        alert(
          "No user found with the provided client email. Please enter a valid email linked to a buyer or seller account!"
        );
      } else {
        console.error("Error submitting buyer request:", error);
        alert("Failed to submit form. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchConstants = async () => {
    try {
      setLoading(true);
      const { forms, grades } = await getConstants();
      // Set values
      setForms(forms);
      setGrades(grades);
    } catch (err) {
      console.log("Unable to fetch constants", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConstants();
  }, []);

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="sectionWrapper mobileWrapper">
      <div>
        <h1 className="text-sm font-bold text-green ml-0.5">Product Form</h1>
        <div className="highlight highlight-md" />
      </div>

      <p className="mt-3 ml-3 text-sm text-muted mobileMargin">
        Interested in Supplying? Fill out this form to introduce your product.
      </p>

      <Form {...form}>
        <form
          className="space-y-4 p-3 mobilePaddingMargin"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          {(user.role === "seller_agent" || user.role === "admin") && (
            <div className="flex justify-between mobileFormWrapper">
              <div className="w-[49%] mobileInputWrapper">
                <FormItem>
                  <FormLabel>Client's Email*</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Enter client's email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </div>
            </div>
          )}

          <div className="flex justify-between mobileFormWrapper">
            {/* Grade */}
            <div className="w-[49%] mobileInputWrapper">
              <FormField
                control={form.control}
                name="grade"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Grade of Material*</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl className="h-10">
                        <SelectTrigger>
                          <SelectValue placeholder="Select grade" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {grades.map((grade) => (
                          <SelectItem key={grade} value={grade}>
                            {grade}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Form */}
            <div className="w-[49%] mobileInputWrapper">
              <FormField
                control={form.control}
                name="form"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Form of Material*</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl className="h-10">
                        <SelectTrigger>
                          <SelectValue placeholder="Select form" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {forms.map((form) => (
                          <SelectItem key={form} value={form}>
                            {form}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mobileFormWrapper">
            {/* Price */}
            <div className="w-[49%] flex justify-between items-end mobileInputWrapper">
              <div className="w-[49%]">
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price*</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="$" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="w-[49%]">
                <FormField
                  control={form.control}
                  name="priceUnit"
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl className="h-10">
                          <SelectTrigger className="text-muted">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {["USD/kg", "USD/lbs"].map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Quantity */}
            <div className="w-[49%] flex justify-between items-end mobileInputWrapper">
              <div className="w-[49%]">
                <FormField
                  control={form.control}
                  name="quantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Quantity*</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Enter quantity"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="w-[49%]">
                <FormField
                  control={form.control}
                  name="quantityUnit"
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl className="h-10">
                          <SelectTrigger className="text-muted">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {["kg", "lbs"].map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between mobileFormWrapper">
            <div className="w-[49%] mobileInputWrapper">
              {/* Postal code */}
              <FormField
                control={form.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postal Code</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter postal code" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="w-[49%] mobileInputWrapper">
              {/* Postal code */}
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter City, State, Country"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mobileFormWrapper">
            {/* Product Description */}
            <div className="w-[100%] mobileInputWrapper">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Describe the product" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mobileFormWrapper">
            {/* Source of inquiry */}
            <div className="w-[100%] mobileInputWrapper">
              <FormField
                control={form.control}
                name="source"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source of inquiry</FormLabel>
                    <FormControl>
                      <Input placeholder="Link to source of inquiry" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Upload Images */}
          <ImageUploadForm images={images} setImages={setImages} />

          {/* Debug State */}
          {/* <pre className="mt-4">{JSON.stringify(images, null, 2)}</pre> */}

          <FormField
            control={form.control}
            name="terms"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-3 text-muted">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    By submitting this form, you grant JP Associates the right
                    to use the submitted data and photos for evaluation,
                    communication, and promotional purposes. All information
                    provided will be handled in accordance with our{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      className="text-blue-500"
                    >
                      Privacy Policy
                    </a>
                    .
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <div className="text-right">
            <Button
              type="submit"
              className="w-[200px] bg-green mobileInputWrapper"
            >
              Submit
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default SellerProductForm;
